'use client'

import React, { useEffect, useRef, forwardRef } from 'react'
import fitty from 'fitty'
import type { FittyInstance } from 'fitty'
import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { type VariantProps, cva } from 'class-variance-authority'
import { cn } from '../helpers/index.js'

// 定义 avatar 的样式变体
const avatarVariants = cva('tracking transition-colors', {
  variants: {
    size: {
      small: 'size-8 text-sm', // 小
      medium: 'size-10 text-xl', // 中
      large: 'size-12 text-2xl', // 大
    },
  },
})

type AvatarProps = {
  /** 定义子组件作为父组件的类型,详细使用方式请参考radix-ui */
  asChild?: boolean
  /** 类名*/
  className?: string
  /** 样式*/
  style?: React.CSSProperties
  /** 点击事件*/
  onClick?: Function
  /** 文本自适应*/
  isAdaptation?: boolean
  /** 文本自适应最小字体, 开启时生效*/
  minSize?: number
  /** 文本自适应最大字体, 开启时生效*/
  maxSize?: number
  /** 文本自适应的类, 开启时生效*/
  textClassName?: string
} & VariantProps<typeof avatarVariants> &
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>

/**
 * Avatar - 头像
 *
 * @description 用户头像组件，支持图片、文字回退、自适应文本大小和多种尺寸
 */
const Avatar = forwardRef<React.ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  (
    {
      className,
      textClassName,
      isAdaptation = false,
      minSize = 4,
      maxSize = 16,
      children,
      style,
      size = 'small',
      onClick,
      ...props
    },
    ref
  ) => {
    const textRef = useRef<HTMLDivElement>(null)
    // 点击事件
    const handleClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      onClick?.(e)
    }

    useEffect(() => {
      let fittyInstance: FittyInstance
      if (isAdaptation && textRef.current) {
        fittyInstance = fitty?.(textRef.current, {
          minSize,
          maxSize,
        })
      }
      return () => fittyInstance?.unsubscribe?.()
    }, [])

    return (
      <AvatarPrimitive.Root
        ref={ref}
        onClick={handleClick}
        className={cn(
          avatarVariants({ size }),
          'bg-container-secondary-0 text-info-primary relative flex shrink-0 items-center justify-center overflow-hidden rounded-full',
          className
        )}
        {...(style ? { style: style } : null)}
        {...props}
      >
        {isAdaptation ? (
          <span className="bg-muted flex size-full items-center justify-center rounded-full">
            <span ref={textRef} className={cn('px-2', textClassName)}>
              {children}
            </span>
          </span>
        ) : (
          children
        )}
      </AvatarPrimitive.Root>
    )
  }
)

Avatar.displayName = 'Avatar'

const AvatarImage = forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image ref={ref} className={cn('aspect-square size-full', className)} loading="lazy" {...props} />
))

AvatarImage.displayName = AvatarPrimitive.Image.displayName

const AvatarFallback = forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => {
  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn('bg-muted flex size-full items-center justify-center rounded-full', className)}
      {...props}
    />
  )
})

AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarImage, AvatarFallback }
